import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Building2,
  ArrowLeft,
  FileText
} from 'lucide-react';

const PAGE_TITLE = 'Sub-Processors — MyPropOps';
const PAGE_DESCRIPTION =
  'Third-party sub-processors engaged by MyPropOps to deliver the platform, including their purposes, locations, and data categories processed.';

const subProcessors = [
  {
    name: 'Railway',
    location: 'United States',
    purpose: 'Application hosting and infrastructure',
    data: 'Application data, audit logs, runtime data',
  },
  {
    name: 'MongoDB Atlas',
    location: 'United States',
    purpose: 'Primary database hosting',
    data: 'All customer data stored in the platform',
  },
  {
    name: 'Stripe',
    location: 'United States',
    purpose: 'Payment processing for subscription billing',
    data: 'Billing contact data, payment metadata; MyPropOps does not store full card numbers',
  },
  {
    name: 'Mailchimp Transactional / Mandrill',
    location: 'United States',
    purpose: 'Transactional email delivery (account notifications, password resets, invitations)',
    data: 'Email addresses, names, message content',
  },
  {
    name: 'Anthropic',
    location: 'United States',
    purpose: 'AI inference for Maintenance Recommendation and Compliance Check features',
    data: 'Anonymized property and inspection data submitted at the time of the request; not stored by Anthropic beyond the inference call',
  },
];

const SubProcessors = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    let metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription ? metaDescription.getAttribute('content') : null;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null && metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">MyPropOps</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button>Start Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/dpa" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Data Processing Agreement
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Sub-Processors</h1>
            </div>
          </div>

          <p className="text-muted-foreground mb-2">
            MyPropOps engages the following third-party sub-processors to deliver the platform. Each sub-processor is contractually bound to data protection terms equivalent to those MyPropOps offers Controllers under our Data Processing Agreement.
          </p>

          <p className="text-sm text-muted-foreground">Last updated: May 8, 2026.</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">

          <Card>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 pr-4 font-medium">Sub-Processor</th>
                      <th className="text-left py-3 pr-4 font-medium">Purpose</th>
                      <th className="text-left py-3 pr-4 font-medium">Location</th>
                      <th className="text-left py-3 font-medium">Data Categories Processed</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground align-top">
                    {subProcessors.map((sp) => (
                      <tr key={sp.name} className="border-b last:border-b-0">
                        <td className="py-3 pr-4 font-medium text-foreground whitespace-nowrap">{sp.name}</td>
                        <td className="py-3 pr-4">{sp.purpose}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">{sp.location}</td>
                        <td className="py-3">{sp.data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">
                MyPropOps will provide at least thirty (30) days' advance notice before adding a new sub-processor that processes Controller personal data. Controllers may object to a new sub-processor on legitimate grounds by contacting <a href="mailto:privacy@mypropops.com" className="text-primary hover:underline">privacy@mypropops.com</a> within fifteen (15) days of the notice.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">
                For more information, see our <Link to="/dpa" className="text-primary hover:underline">Data Processing Agreement</Link> and <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
              </p>
            </CardContent>
          </Card>

        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-border mt-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            <span className="font-semibold">MyPropOps</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/dpa" className="hover:text-foreground">DPA</Link>
            <Link to="/security" className="hover:text-foreground">Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SubProcessors;
